import { useEffect } from 'react';
import {
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, getMapTileSource } from './mapTileSource';

export interface GeoMapMarker {
  key: string;
  lat: number;
  lng: number;
  /** Text uvnitř markeru ('★' pro hlavní bod, jinak pořadové číslo). */
  label: string;
  /** Barva pozadí markeru. Bez ní se odvodí z {@link isMain} (hlavní = červená, jinak šedá). */
  color?: string;
  /** Hlavní bod (větší, v hub režimu střed spojnic). */
  isMain?: boolean;
  /** Text tooltipu při najetí (název stanice apod.). */
  tooltip?: string;
}

/** Spojnice mezi body: hub-and-spoke (stanice) · sekvenční linie (trasa) · žádné. */
export type GeoMapConnections = 'hub' | 'sequence' | 'none';

export interface GeoMapProps {
  markers: GeoMapMarker[];
  /** Klik do mapy (aktivní jen v některém režimu) — předá souřadnice. */
  onMapClick?: (lat: number, lng: number) => void;
  /** Klik na marker. */
  onMarkerClick?: (key: string) => void;
  /** Inkrementací se vyžádá „Vystředit" (fitBounds na všechny body). */
  fitSignal: number;
  /**
   * Spojnice mezi body: `hub` = přerušované od hlavního bodu ke všem (default, agenda Stanice),
   * `sequence` = jedna linie body v pořadí (agenda Trasy), `none` = bez linií.
   */
  connections?: GeoMapConnections;
  /** Barva spojnic. Default šedá (hub); u `sequence` typicky barva trasy. */
  lineColor?: string;
}

const DEFAULT_MAIN_COLOR = '#b91c1c';
const DEFAULT_DOT_COLOR = '#374151';
const DEFAULT_LINE_COLOR = '#9ca3af';

function markerColor(marker: GeoMapMarker): string {
  return marker.color ?? (marker.isMain ? DEFAULT_MAIN_COLOR : DEFAULT_DOT_COLOR);
}

function buildIcon(marker: GeoMapMarker): L.DivIcon {
  const background = markerColor(marker);
  const size = marker.isMain ? 30 : 26;
  return L.divIcon({
    className: 'phx-geo-marker',
    html:
      `<div style="display:flex;align-items:center;justify-content:center;` +
      `width:${size}px;height:${size}px;border-radius:50%;background:${background};` +
      `color:#fff;font-size:13px;font-weight:600;border:2px solid #fff;` +
      `box-shadow:0 1px 4px rgba(0,0,0,.45)">${marker.label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function ClickHandler({ onMapClick }: { onMapClick?: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(event) {
      onMapClick?.(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

function FitBounds({ markers, fitSignal }: { markers: GeoMapMarker[]; fitSignal: number }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) {
      return;
    }
    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(markers.map((marker) => [marker.lat, marker.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    // Vystředit jen na explicitní signál (ne při každém pohybu markeru).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitSignal]);
  return null;
}

function Connections({
  markers,
  connections,
  lineColor,
}: {
  markers: GeoMapMarker[];
  connections: GeoMapConnections;
  lineColor?: string;
}) {
  if (connections === 'none' || markers.length < 2) {
    return null;
  }

  if (connections === 'sequence') {
    return (
      <Polyline
        positions={markers.map((marker) => [marker.lat, marker.lng])}
        pathOptions={{ color: lineColor ?? DEFAULT_DOT_COLOR, weight: 3 }}
      />
    );
  }

  // hub: přerušované spojnice z hlavního bodu ke všem ostatním.
  const main = markers.find((marker) => marker.isMain);
  if (!main) {
    return null;
  }
  return (
    <>
      {markers
        .filter((marker) => !marker.isMain)
        .map((marker) => (
          <Polyline
            key={`spoke-${marker.key}`}
            positions={[
              [main.lat, main.lng],
              [marker.lat, marker.lng],
            ]}
            pathOptions={{ color: lineColor ?? DEFAULT_LINE_COLOR, weight: 1, dashArray: '4 5' }}
          />
        ))}
    </>
  );
}

/**
 * Sdílená mapová komponenta (OpenStreetMap dlaždice přes přepínatelný `mapTileSource`). Prop-driven
 * geo-primitivum bez doménových předpokladů: číslované/★ markery (volitelná barva + tooltip),
 * spojnice (`hub`/`sequence`/`none`), klik do mapy a na marker, „Vystředit" přes `fitSignal`.
 */
export function GeoMap({
  markers,
  onMapClick,
  onMarkerClick,
  fitSignal,
  connections = 'hub',
  lineColor,
}: GeoMapProps) {
  const tiles = getMapTileSource();
  const center = markers.find((marker) => marker.isMain) ?? markers[0];

  return (
    <MapContainer
      center={center ? [center.lat, center.lng] : DEFAULT_MAP_CENTER}
      zoom={center ? 12 : DEFAULT_MAP_ZOOM}
      className="h-full min-h-70 w-full"
    >
      <TileLayer url={tiles.url} attribution={tiles.attribution} maxZoom={tiles.maxZoom} />
      <ClickHandler onMapClick={onMapClick} />
      <FitBounds markers={markers} fitSignal={fitSignal} />
      <Connections markers={markers} connections={connections} lineColor={lineColor} />
      {markers.map((marker) => (
        <Marker
          key={marker.key}
          position={[marker.lat, marker.lng]}
          icon={buildIcon(marker)}
          eventHandlers={{ click: () => onMarkerClick?.(marker.key) }}
        >
          {marker.tooltip ? <Tooltip>{marker.tooltip}</Tooltip> : null}
        </Marker>
      ))}
    </MapContainer>
  );
}
